import { BaseTool } from './base';
import { Events } from '../enums';

import {
  getEnabledElement,
  StackViewport,
  Enums,
  utilities as csUtils,
} from '@cornerstonejs/core';
import type { Types } from '@cornerstonejs/core';
import type { EventTypes, PublicToolProps, ToolProps, IPoints } from '../types';
import { getViewportIdsWithToolToRender } from '../utilities/viewportFilters';
import triggerAnnotationRenderForViewportIds from '../utilities/triggerAnnotationRenderForViewportIds';
import { getNativeSourceProperties } from '../utilities/genericViewportToolHelpers';
import { state } from '../store/state';
import { vec3 } from 'gl-matrix';

import {
  hideElementCursor,
  resetElementCursor,
} from '../cursors/elementCursor';

const MAGNIFY_VIEWPORT_ID = 'magnify-viewport';

class MagnifyTool extends BaseTool {
  static toolName;
  editData: {
    referencedImageId: string;
    viewportIdsToRender: string[];
    enabledElement: Types.IEnabledElement;
    renderingEngine: Types.IRenderingEngine;
    currentPoints: IPoints;
    isTouch: boolean;
  } | null;

  constructor(
    toolProps: PublicToolProps = {},
    defaultToolProps: ToolProps = {
      supportedInteractionTypes: ['Mouse', 'Touch'],
      configuration: {
        magnifySize: 10, // parallel scale , higher more zoom
        magnifyWidth: 250, //px
        magnifyHeight: 250, //px
        // On touch, the loupe is lifted above the contact point; this is the
        // gap in px between the contact point and the loupe's bottom edge.
        touchOffset: 40, //px
      },
    }
  ) {
    super(toolProps, defaultToolProps);
  }

  private _hasBeenRemoved = false;

  _getReferencedImageId(viewport: Types.IViewport): string | undefined {
    const targetId = this.getTargetId(viewport);

    let referencedImageId: string | undefined;

    if (viewport instanceof StackViewport) {
      referencedImageId = targetId.split('imageId:')[1];
    } else if (csUtils.isGenericViewport(viewport)) {
      // Native PLANAR_NEXT stack-mode viewport: the current image id directly.
      referencedImageId = getNativeSourceProperties(viewport).currentImageId;
    }

    return referencedImageId;
  }

  preMouseDownCallback = (evt: EventTypes.InteractionEventType) => {
    const eventDetail = evt.detail;
    const { element, currentPoints } = eventDetail;
    const enabledElement = getEnabledElement(element);
    const { viewport, renderingEngine } = enabledElement;

    if (
      !(viewport instanceof StackViewport) &&
      !csUtils.isGenericViewport(viewport)
    ) {
      throw new Error(
        'MagnifyTool only works on Stack or native planar viewports'
      );
    }

    const referencedImageId = this._getReferencedImageId(viewport);

    if (!referencedImageId) {
      throw new Error(
        'MagnifyTool: No referenced image id found, reconstructed planes not supported yet'
      );
    }

    const viewportIdsToRender = getViewportIdsWithToolToRender(
      element,
      this.getToolName()
    );

    // True when reached via preTouchStartCallback (explicit alias or the
    // dispatcher-level touch fallback) — both deliver the TOUCH_START event.
    const isTouch = evt.type === Events.TOUCH_START;

    this.editData = {
      referencedImageId,
      viewportIdsToRender,
      enabledElement,
      renderingEngine,
      currentPoints,
      isTouch,
    };

    this._createMagnificationViewport();
    this._activateDraw(element);

    hideElementCursor(element);

    evt.preventDefault();

    triggerAnnotationRenderForViewportIds(viewportIdsToRender);

    return true;
  };

  preTouchStartCallback = (evt: EventTypes.InteractionEventType) => {
    this.preMouseDownCallback(evt);
  };

  /**
   * Positions the magnify loupe element relative to the interaction point.
   * Mouse: centered on the pointer (unchanged legacy behavior). Touch: lifted
   * above the contact point by `configuration.touchOffset` so the finger does
   * not occlude the loupe, clamped to the viewport bounds.
   */
  private _positionMagnifyElement = (
    magnifyElement: HTMLDivElement,
    canvasPos: Types.Point2,
    element: HTMLDivElement
  ) => {
    const { magnifyWidth, magnifyHeight, touchOffset } = this.configuration;

    let left = canvasPos[0] - magnifyWidth / 2;
    let top = canvasPos[1] - magnifyHeight / 2;

    if (this.editData?.isTouch) {
      top = canvasPos[1] - magnifyHeight - (touchOffset ?? 0);

      const maxLeft = Math.max(element.clientWidth - magnifyWidth, 0);
      const maxTop = Math.max(element.clientHeight - magnifyHeight, 0);

      left = Math.min(Math.max(left, 0), maxLeft);
      top = Math.min(Math.max(top, 0), maxTop);
    }

    magnifyElement.style.top = `${top}px`;
    magnifyElement.style.left = `${left}px`;
  };

  _createMagnificationViewport = () => {
    const {
      enabledElement,
      referencedImageId,
      viewportIdsToRender,
      renderingEngine,
      currentPoints,
    } = this.editData;
    const { viewport } = enabledElement;
    const { element } = viewport;
    // The loupe mirrors the source viewport's appearance. Native PLANAR_NEXT has no
    // getProperties/getViewPresentation; read its VOI/LUT via getDisplaySetPresentation
    // and its rotation/flip via the projection-aware helper.
    const native = csUtils.isGenericViewport(viewport)
      ? getNativeSourceProperties(viewport)
      : undefined;
    const viewportProperties = native
      ? native.properties
      : viewport.getProperties();
    const {
      rotation: originalViewportRotation,
      flipHorizontal: originalViewportFlipHorizontal,
      flipVertical: originalViewportFlipVertical,
    } = native ?? viewport.getViewPresentation();

    const { canvas: canvasPos, world: worldPos } = currentPoints;

    let magnifyToolElement: HTMLDivElement;

    magnifyToolElement = element.querySelector('.magnifyTool');
    if (magnifyToolElement === null) {
      const magnifyElement = document.createElement('div');

      magnifyElement.classList.add('magnifyTool');

      magnifyElement.style.display = 'block';
      magnifyElement.style.width = `${this.configuration.magnifyWidth}px`;
      magnifyElement.style.height = `${this.configuration.magnifyHeight}px`;
      magnifyElement.style.position = 'absolute';

      magnifyToolElement = magnifyElement;

      const viewportElement = element.querySelector('.viewport-element');
      viewportElement.appendChild(magnifyElement);

      const viewportInput = {
        viewportId: MAGNIFY_VIEWPORT_ID,
        type: Enums.ViewportType.STACK,
        element: magnifyToolElement as HTMLDivElement,
      };

      renderingEngine.enableElement(viewportInput);
    }

    // Todo: use CSS transform instead of setting top and left for better performance
    this._positionMagnifyElement(magnifyToolElement, canvasPos, element);

    const magnifyViewport = renderingEngine.getViewport(
      MAGNIFY_VIEWPORT_ID
    ) as Types.IStackViewport;
    magnifyViewport.setStack([referencedImageId]).then(() => {
      if (this._hasBeenRemoved) {
        return;
      }
      // match the original viewport voi range
      magnifyViewport.setProperties(viewportProperties);

      // match the original viewport's rotation and flip state
      magnifyViewport.setViewPresentation({
        rotation: originalViewportRotation,
        flipHorizontal: originalViewportFlipHorizontal,
        flipVertical: originalViewportFlipVertical,
      });

      const { focalPoint, position, viewPlaneNormal } =
        magnifyViewport.getCamera();

      // Use the source viewport's parallelScale as the magnification base.
      let parallelScale: number;
      if (csUtils.isGenericViewport(viewport)) {
        // Native PLANAR_NEXT has no getCamera/parallelScale and its on-screen canvas is
        // hidden, so reconstruct the source's actual on-screen parallelScale geometrically:
        // worldPerPixel (vertical) from canvasToWorld of two element-space points 1px apart,
        // then parallelScale = worldPerPixel * elementHeight / 2. This tracks the source's
        // pan/zoom (matching legacy), unlike the loupe's own fit parallelScale.
        const sourceElement = viewport.element as HTMLElement;
        const worldTop = viewport.canvasToWorld([0, 0] as Types.Point2);
        const worldBottom = viewport.canvasToWorld([0, 1] as Types.Point2);
        const worldPerPixel = vec3.distance(
          worldTop as Types.Point3,
          worldBottom as Types.Point3
        );
        parallelScale = (worldPerPixel * sourceElement.clientHeight) / 2;
      } else {
        ({ parallelScale } = viewport.getCamera());
      }

      const distance = Math.sqrt(
        Math.pow(focalPoint[0] - position[0], 2) +
          Math.pow(focalPoint[1] - position[1], 2) +
          Math.pow(focalPoint[2] - position[2], 2)
      );

      const updatedFocalPoint = <Types.Point3>[
        worldPos[0],
        worldPos[1],
        worldPos[2],
      ];

      const updatedPosition = <Types.Point3>[
        updatedFocalPoint[0] + distance * viewPlaneNormal[0],
        updatedFocalPoint[1] + distance * viewPlaneNormal[1],
        updatedFocalPoint[2] + distance * viewPlaneNormal[2],
      ];

      magnifyViewport.setCamera({
        parallelScale: parallelScale * (1 / this.configuration.magnifySize),
        focalPoint: updatedFocalPoint,
        position: updatedPosition,
      });
      magnifyViewport.render();
    });

    magnifyToolElement.style.display = 'block';
    triggerAnnotationRenderForViewportIds(viewportIdsToRender);
  };

  _cancelCallback = (evt: EventTypes.InteractionEventType) => {
    // Empêche l'affichage du menu contextuel par défaut du navigateur
    evt.preventDefault();
    evt.stopPropagation();

    // Appelle la fonction qui désactive et nettoie l'outil
    this._dragEndCallback(evt);
  };

  _dragCallback = (evt: EventTypes.InteractionEventType) => {
    const eventDetail = evt.detail;

    const { deltaPoints, element, currentPoints } = eventDetail;
    const deltaPointsWorld = deltaPoints.world;
    const canvasPos = currentPoints.canvas;
    const enabledElement = getEnabledElement(element);
    const { renderingEngine } = enabledElement;

    const magnifyViewport = renderingEngine.getViewport(MAGNIFY_VIEWPORT_ID);

    const magnifyElement = element.querySelector(
      '.magnifyTool'
    ) as HTMLDivElement;

    if (!magnifyElement) {
      return;
    }

    this._positionMagnifyElement(magnifyElement, canvasPos, element);

    const { focalPoint, position } = magnifyViewport.getCamera();

    const updatedPosition = <Types.Point3>[
      position[0] + deltaPointsWorld[0],
      position[1] + deltaPointsWorld[1],
      position[2] + deltaPointsWorld[2],
    ];

    const updatedFocalPoint = <Types.Point3>[
      focalPoint[0] + deltaPointsWorld[0],
      focalPoint[1] + deltaPointsWorld[1],
      focalPoint[2] + deltaPointsWorld[2],
    ];

    magnifyViewport.setCamera({
      focalPoint: updatedFocalPoint,
      position: updatedPosition,
    });

    magnifyViewport.render();
  };

  _dragEndCallback = (evt: EventTypes.InteractionEventType) => {
    let { element } = evt.detail;

    if (element === undefined) {
      const { enabledElement } = this.editData;

      const { viewport } = enabledElement;
      element = viewport.element;
    }

    const enabledElement = getEnabledElement(element);
    const { renderingEngine } = enabledElement;

    renderingEngine.disableElement(MAGNIFY_VIEWPORT_ID);

    const viewportElement = element.querySelector('.viewport-element');

    const magnifyToolElement = viewportElement.querySelector(
      '.magnifyTool'
    ) as HTMLDivElement;

    viewportElement.removeChild(magnifyToolElement);

    this._deactivateDraw(element);
    resetElementCursor(element);
    this._hasBeenRemoved = true;
  };

  _activateDraw = (element: HTMLDivElement) => {
    state.isInteractingWithTool = true;
    this._hasBeenRemoved = false;

    element.addEventListener(
      Events.MOUSE_UP,
      this._dragEndCallback as EventListener
    );
    element.addEventListener(
      Events.MOUSE_DRAG,
      this._dragCallback as EventListener
    );
    element.addEventListener(
      Events.MOUSE_CLICK,
      this._dragEndCallback as EventListener
    );

    element.addEventListener(
      'contextmenu',
      this._cancelCallback as EventListener
    );

    element.addEventListener(
      Events.TOUCH_END,
      this._dragEndCallback as EventListener
    );
    element.addEventListener(
      Events.TOUCH_DRAG,
      this._dragCallback as EventListener
    );
  };

  _deactivateDraw = (element: HTMLDivElement) => {
    state.isInteractingWithTool = false;

    element.removeEventListener(
      Events.MOUSE_UP,
      this._dragEndCallback as EventListener
    );
    element.removeEventListener(
      Events.MOUSE_DRAG,
      this._dragCallback as EventListener
    );
    element.removeEventListener(
      Events.MOUSE_CLICK,
      this._dragEndCallback as EventListener
    );

    element.removeEventListener(
      'contextmenu',
      this._cancelCallback as EventListener
    );
    element.removeEventListener(
      Events.TOUCH_END,
      this._dragEndCallback as EventListener
    );
    element.removeEventListener(
      Events.TOUCH_DRAG,
      this._dragCallback as EventListener
    );
  };
}

MagnifyTool.toolName = 'Magnify';
export default MagnifyTool;
